import { db } from "./dbClient";
import { supabaseStorageCompat as supabase } from "./fileStorage";

import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useTheme } from "./theme";
import { saveCompany } from "./Auth";
import SLAConfiguration from "./SettingsPages/SLAConfiguration";
import CaseRiskSLAConfig from "./SettingsPages/CaseRiskSLAConfig";
import MonitoringConfiguration from "./SettingsPages/MonitoringConfiguration";
import SourceManagement from "./SettingsPages/SourceManagement";
import RiskConfiguration from "./SettingsPages/RiskConfiguration";
import AMLModelConfiguration from "./SettingsPages/AMLModelConfiguration";

const JURISDICTIONS = ["United Kingdom (FCA)", "United States (FinCEN)", "European Union (EBA)", "Singapore (MAS)", "UAE (CBUAE)", "Hong Kong (HKMA)", "Cayman Islands (CIMA)", "Malta (MFSA)", "Brazil (BCB)", "Other"];

const RETENTION_RULES = {
  "United Kingdom (FCA)": [
    { table: "transactions", label: "Transactions", years: 5, regulation: "FCA AML Rules" },
    { table: "cases", label: "Case Management", years: 7, regulation: "FCA AML Rules" },
    { table: "sar_drafts", label: "SAR Filings", years: 5, regulation: "FCA MLRO" },
    { table: "ctr_filings", label: "CTR Filings", years: 6, regulation: "FATF" },
    { table: "kyc_submissions", label: "KYC Records", years: 6, regulation: "FCA AML" },
    { table: "aml_alerts", label: "AML Alerts", years: 7, regulation: "FCA AML Rules" },
    { table: "transaction_logs", label: "Transaction Logs", years: 5, regulation: "FCA AML" },
    { table: "audit_logs", label: "Audit Logs", years: 6, regulation: "UK GDPR" },
  ],
  "United States (FinCEN)": [
    { table: "transactions", label: "Transactions", years: 5, regulation: "31 CFR 1010.410" },
    { table: "cases", label: "Case Management", years: 7, regulation: "FinCEN AML" },
    { table: "sar_drafts", label: "SAR Filings", years: 5, regulation: "31 CFR 1020.210" },
    { table: "ctr_filings", label: "CTR Filings", years: 6, regulation: "31 CFR 1010.430" },
    { table: "kyc_submissions", label: "KYC Records", years: 5, regulation: "FinCEN KYC" },
    { table: "aml_alerts", label: "AML Alerts", years: 7, regulation: "FinCEN AML" },
    { table: "transaction_logs", label: "Transaction Logs", years: 5, regulation: "BSA" },
    { table: "audit_logs", label: "Audit Logs", years: 7, regulation: "Gramm-Leach-Bliley" },
  ],
  "European Union (EBA)": [
    { table: "transactions", label: "Transactions", years: 5, regulation: "AMLD5" },
    { table: "cases", label: "Case Management", years: 7, regulation: "EU AML Directive" },
    { table: "sar_drafts", label: "SAR Filings", years: 5, regulation: "AMLD5" },
    { table: "ctr_filings", label: "CTR Filings", years: 6, regulation: "FATF" },
    { table: "kyc_submissions", label: "KYC Records", years: 5, regulation: "AMLD5" },
    { table: "aml_alerts", label: "AML Alerts", years: 7, regulation: "EBA Guidelines" },
    { table: "transaction_logs", label: "Transaction Logs", years: 5, regulation: "AMLD5" },
    { table: "audit_logs", label: "Audit Logs", years: 3, regulation: "GDPR" },
  ],
  "Singapore (MAS)": [
    { table: "transactions", label: "Transactions", years: 5, regulation: "MAS Guidelines" },
    { table: "cases", label: "Case Management", years: 5, regulation: "Singapore MLASFTA" },
    { table: "sar_drafts", label: "SAR Filings", years: 5, regulation: "MAS Guidelines" },
    { table: "ctr_filings", label: "CTR Filings", years: 5, regulation: "FATF" },
    { table: "kyc_submissions", label: "KYC Records", years: 5, regulation: "MAS Guidelines" },
    { table: "aml_alerts", label: "AML Alerts", years: 5, regulation: "Singapore AML" },
    { table: "transaction_logs", label: "Transaction Logs", years: 5, regulation: "MAS AML" },
    { table: "audit_logs", label: "Audit Logs", years: 5, regulation: "Singapore PDPA" },
  ],
  "UAE (CBUAE)": [
    { table: "transactions", label: "Transactions", years: 5, regulation: "CBUAE AML" },
    { table: "cases", label: "Case Management", years: 7, regulation: "CBUAE AML" },
    { table: "sar_drafts", label: "SAR Filings", years: 7, regulation: "CBUAE Guidelines" },
    { table: "ctr_filings", label: "CTR Filings", years: 6, regulation: "FATF" },
    { table: "kyc_submissions", label: "KYC Records", years: 6, regulation: "CBUAE AML" },
    { table: "aml_alerts", label: "AML Alerts", years: 7, regulation: "CBUAE AML" },
    { table: "transaction_logs", label: "Transaction Logs", years: 5, regulation: "CBUAE AML" },
    { table: "audit_logs", label: "Audit Logs", years: 7, regulation: "UAE Data Law" },
  ],
  "Hong Kong (HKMA)": [
    { table: "transactions", label: "Transactions", years: 5, regulation: "HKMA AML" },
    { table: "cases", label: "Case Management", years: 6, regulation: "Hong Kong AMLO" },
    { table: "sar_drafts", label: "SAR Filings", years: 5, regulation: "AMLO" },
    { table: "ctr_filings", label: "CTR Filings", years: 6, regulation: "FATF" },
    { table: "kyc_submissions", label: "KYC Records", years: 6, regulation: "HKMA Guidelines" },
    { table: "aml_alerts", label: "AML Alerts", years: 6, regulation: "Hong Kong AML" },
    { table: "transaction_logs", label: "Transaction Logs", years: 5, regulation: "HKMA AML" },
    { table: "audit_logs", label: "Audit Logs", years: 5, regulation: "Hong Kong PDPO" },
  ],
  "Cayman Islands (CIMA)": [
    { table: "transactions", label: "Transactions", years: 6, regulation: "CIMA AML" },
    { table: "cases", label: "Case Management", years: 6, regulation: "CIMA AML" },
    { table: "sar_drafts", label: "SAR Filings", years: 6, regulation: "CIMA MLRO" },
    { table: "ctr_filings", label: "CTR Filings", years: 6, regulation: "FATF" },
    { table: "kyc_submissions", label: "KYC Records", years: 6, regulation: "CIMA AML" },
    { table: "aml_alerts", label: "AML Alerts", years: 6, regulation: "CIMA AML" },
    { table: "transaction_logs", label: "Transaction Logs", years: 6, regulation: "CIMA AML" },
    { table: "audit_logs", label: "Audit Logs", years: 6, regulation: "Cayman Privacy Law" },
  ],
  "Malta (MFSA)": [
    { table: "transactions", label: "Transactions", years: 5, regulation: "AMLD5" },
    { table: "cases", label: "Case Management", years: 7, regulation: "Malta AML" },
    { table: "sar_drafts", label: "SAR Filings", years: 5, regulation: "AMLD5" },
    { table: "ctr_filings", label: "CTR Filings", years: 6, regulation: "FATF" },
    { table: "kyc_submissions", label: "KYC Records", years: 5, regulation: "AMLD5" },
    { table: "aml_alerts", label: "AML Alerts", years: 7, regulation: "Malta AML" },
    { table: "transaction_logs", label: "Transaction Logs", years: 5, regulation: "AMLD5" },
    { table: "audit_logs", label: "Audit Logs", years: 3, regulation: "GDPR" },
  ],
  "Brazil (BCB)": [
    { table: "transactions", label: "Transactions", years: 5, regulation: "BCB AML Guidelines" },
    { table: "cases", label: "Case Management", years: 5, regulation: "Brazil Law 12683" },
    { table: "sar_drafts", label: "SAR Filings", years: 5, regulation: "BCB Resolution 4957" },
    { table: "ctr_filings", label: "CTR Filings", years: 5, regulation: "FATF" },
    { table: "kyc_submissions", label: "KYC Records", years: 5, regulation: "BCB AML Guidelines" },
    { table: "aml_alerts", label: "AML Alerts", years: 5, regulation: "BCB AML" },
    { table: "transaction_logs", label: "Transaction Logs", years: 5, regulation: "BCB AML" },
    { table: "audit_logs", label: "Audit Logs", years: 5, regulation: "Brazil LGPD" },
  ],
  "Other": [
    { table: "transactions", label: "Transactions", years: 5, regulation: "FATF" },
    { table: "cases", label: "Case Management", years: 7, regulation: "FATF" },
    { table: "sar_drafts", label: "SAR Filings", years: 5, regulation: "FATF" },
    { table: "ctr_filings", label: "CTR Filings", years: 6, regulation: "FATF" },
    { table: "kyc_submissions", label: "KYC Records", years: 5, regulation: "FATF" },
    { table: "aml_alerts", label: "AML Alerts", years: 7, regulation: "FATF" },
    { table: "transaction_logs", label: "Transaction Logs", years: 5, regulation: "FATF" },
    { table: "audit_logs", label: "Audit Logs", years: 3, regulation: "International Standards" },
  ],
};

const RETENTION_SQL = `-- ========================================================================
-- CROSSREGGUARD - COMPLETE DATABASE SCHEMA SETUP
-- Generated: August 10, 2026 | Neon-Compatible
-- ========================================================================

-- STEP 1: ENABLE EXTENSIONS
-- ========================================================================
CREATE EXTENSION IF NOT EXISTS "uuid-ossp" WITH SCHEMA public;

-- Note: pg_cron is not available on Neon. For data retention, use
-- external scheduled jobs (GitHub Actions, AWS Lambda, etc) or
-- contact Neon support to enable pg_cron on your project.

-- STEP 2: CREATE ALL TABLES FIRST (NO INDEXES)
-- ========================================================================

CREATE TABLE IF NOT EXISTS public.todos (
    id uuid DEFAULT public.uuid_generate_v4() PRIMARY KEY,
    company_id uuid NOT NULL REFERENCES public.companies(id) ON DELETE CASCADE,
    user_id uuid REFERENCES public.users(id) ON DELETE SET NULL,
    title text NOT NULL,
    description text,
    status text DEFAULT 'open',
    priority text DEFAULT 'medium',
    due_date timestamp with time zone,
    completed_at timestamp with time zone,
    assigned_to uuid,
    category text,
    tags text[],
    metadata jsonb,
    created_at timestamp with time zone DEFAULT now(),
    updated_at timestamp with time zone DEFAULT now(),
    created_by uuid
);

CREATE TABLE IF NOT EXISTS public.watchlist (
    id uuid DEFAULT public.uuid_generate_v4() PRIMARY KEY,
    company_id uuid NOT NULL REFERENCES public.companies(id) ON DELETE CASCADE,
    entity_type text NOT NULL,
    entity_name text NOT NULL,
    entity_value text,
    list_type text DEFAULT 'internal',
    reason text,
    risk_level text DEFAULT 'medium',
    status text DEFAULT 'active',
    source text,
    source_ref text,
    screening_date timestamp with time zone,
    match_percentage numeric(5, 2),
    match_details jsonb,
    added_by uuid,
    resolved_by uuid,
    resolved_at timestamp with time zone,
    notes text,
    created_at timestamp with time zone DEFAULT now(),
    updated_at timestamp with time zone DEFAULT now()
);

CREATE TABLE IF NOT EXISTS public.customers (
    id uuid DEFAULT public.uuid_generate_v4() PRIMARY KEY,
    company_id uuid NOT NULL REFERENCES public.companies(id) ON DELETE CASCADE,
    customer_type text NOT NULL,
    first_name text,
    last_name text,
    full_name text NOT NULL,
    business_name text,
    email text,
    phone text,
    date_of_birth date,
    nationality text,
    country_of_residence text,
    tax_id text,
    ssn_last4 text,
    kyc_status text DEFAULT 'pending',
    kyc_submission_id uuid REFERENCES public.kyc_submissions(id) ON DELETE SET NULL,
    edd_required boolean DEFAULT false,
    edd_status text,
    risk_score integer DEFAULT 0,
    risk_rating text DEFAULT 'medium',
    pep boolean DEFAULT false,
    pep_type text,
    sanctions_match boolean DEFAULT false,
    adverse_media_match boolean DEFAULT false,
    onboarding_date timestamp with time zone,
    last_review_date timestamp with time zone,
    last_review_by uuid,
    next_review_date timestamp with time zone,
    status text DEFAULT 'active',
    compliance_notes text,
    metadata jsonb,
    created_at timestamp with time zone DEFAULT now(),
    updated_at timestamp with time zone DEFAULT now(),
    created_by uuid
);

CREATE TABLE IF NOT EXISTS public.ctrs (
    id uuid DEFAULT public.uuid_generate_v4() PRIMARY KEY,
    company_id uuid NOT NULL REFERENCES public.companies(id) ON DELETE CASCADE,
    filer_name text NOT NULL,
    filer_ein text,
    currency_code text DEFAULT 'USD',
    transaction_date date NOT NULL,
    amount numeric(15, 2) NOT NULL,
    transaction_type text,
    reporting_reason text,
    customer_id uuid REFERENCES public.customers(id) ON DELETE SET NULL,
    customer_name text,
    customer_address text,
    status text DEFAULT 'draft',
    filing_date timestamp with time zone,
    irs_reference_number text,
    reviewer_notes text,
    reviewed_by uuid,
    reviewed_at timestamp with time zone,
    filed_by uuid,
    created_at timestamp with time zone DEFAULT now(),
    updated_at timestamp with time zone DEFAULT now()
);

CREATE TABLE IF NOT EXISTS public.sars (
    id uuid DEFAULT public.uuid_generate_v4() PRIMARY KEY,
    company_id uuid NOT NULL REFERENCES public.companies(id) ON DELETE CASCADE,
    case_id uuid REFERENCES public.cases(id) ON DELETE SET NULL,
    filing_institution_name text NOT NULL,
    filing_institution_ein text,
    subject_name text NOT NULL,
    subject_address text,
    customer_id uuid REFERENCES public.customers(id) ON DELETE SET NULL,
    suspicious_activity_type text,
    description_of_activity text,
    amount_involved numeric(15, 2),
    currency text DEFAULT 'USD',
    activity_date date,
    discovered_date date,
    reported_to_law_enforcement boolean DEFAULT false,
    law_enforcement_reference text,
    status text DEFAULT 'draft',
    priority text DEFAULT 'medium',
    priority_justification text,
    filed_date timestamp with time zone,
    irs_reference_number text,
    filing_confirmation text,
    reviewer_notes text,
    reviewed_by uuid,
    reviewed_at timestamp with time zone,
    filed_by uuid,
    supporting_documents jsonb,
    created_at timestamp with time zone DEFAULT now(),
    updated_at timestamp with time zone DEFAULT now()
);

CREATE TABLE IF NOT EXISTS public.crypto_watchlist (
    id uuid DEFAULT public.uuid_generate_v4() PRIMARY KEY,
    company_id uuid NOT NULL REFERENCES public.companies(id) ON DELETE CASCADE,
    user_id uuid,
    address text NOT NULL,
    chain text NOT NULL,
    label text,
    risk_threshold integer DEFAULT 50,
    alert_enabled boolean DEFAULT true,
    last_checked timestamp with time zone,
    last_alert timestamp with time zone,
    metadata jsonb,
    created_at timestamp with time zone DEFAULT now(),
    updated_at timestamp with time zone DEFAULT now()
);

CREATE TABLE IF NOT EXISTS public.crypto_alerts (
    id uuid DEFAULT public.uuid_generate_v4() PRIMARY KEY,
    company_id uuid NOT NULL REFERENCES public.companies(id) ON DELETE CASCADE,
    address text NOT NULL,
    chain text NOT NULL,
    alert_type text,
    risk_score integer,
    message text,
    details jsonb,
    watchlist_id uuid REFERENCES public.crypto_watchlist(id) ON DELETE SET NULL,
    status text DEFAULT 'new',
    created_at timestamp with time zone DEFAULT now()
);

CREATE TABLE IF NOT EXISTS public.compliance_calendar (
    id uuid DEFAULT public.uuid_generate_v4() PRIMARY KEY,
    company_id uuid NOT NULL REFERENCES public.companies(id) ON DELETE CASCADE,
    event_title text NOT NULL,
    event_date date NOT NULL,
    event_type text,
    jurisdiction text,
    description text,
    priority text DEFAULT 'medium',
    assigned_to uuid REFERENCES public.users(id) ON DELETE SET NULL,
    status text DEFAULT 'pending',
    completed_at timestamp with time zone,
    notes text,
    created_at timestamp with time zone DEFAULT now(),
    updated_at timestamp with time zone DEFAULT now(),
    created_by uuid
);

-- STEP 2B: CREATE INDEXES (AFTER ALL TABLES EXIST)
-- ========================================================================
-- Note: If running one query at a time, these are optional for now
-- Indexes improve performance but aren't required for tables to work

-- CREATE INDEX IF NOT EXISTS idx_todos_company ON public.todos(company_id);
-- CREATE INDEX IF NOT EXISTS idx_todos_status ON public.todos(status);
-- CREATE INDEX IF NOT EXISTS idx_watchlist_company ON public.watchlist(company_id);
-- CREATE INDEX IF NOT EXISTS idx_watchlist_entity_name ON public.watchlist(entity_name);
-- CREATE INDEX IF NOT EXISTS idx_watchlist_status ON public.watchlist(status);
-- CREATE INDEX IF NOT EXISTS idx_customers_company ON public.customers(company_id);
-- CREATE INDEX IF NOT EXISTS idx_customers_risk_score ON public.customers(risk_score);
-- CREATE INDEX IF NOT EXISTS idx_ctrs_company ON public.ctrs(company_id);
-- CREATE INDEX IF NOT EXISTS idx_ctrs_customer_id ON public.ctrs(customer_id);
-- CREATE INDEX IF NOT EXISTS idx_ctrs_filing_date ON public.ctrs(filing_date);
-- CREATE INDEX IF NOT EXISTS idx_sars_company ON public.sars(company_id);
-- CREATE INDEX IF NOT EXISTS idx_sars_filed_date ON public.sars(filed_date);
-- CREATE INDEX IF NOT EXISTS idx_crypto_watchlist_company ON public.crypto_watchlist(company_id);
-- CREATE INDEX IF NOT EXISTS idx_crypto_alerts_company ON public.crypto_alerts(company_id);

-- STEP 2C: CREATE MISSING TABLE
-- ========================================================================
CREATE TABLE IF NOT EXISTS public.training_assignments (
    id uuid DEFAULT public.uuid_generate_v4() PRIMARY KEY,
    company_id uuid NOT NULL REFERENCES public.companies(id) ON DELETE CASCADE,
    user_id uuid NOT NULL REFERENCES public.users(id) ON DELETE CASCADE,
    training_module_id uuid NOT NULL REFERENCES public.training_modules(id) ON DELETE CASCADE,
    assigned_date timestamp with time zone DEFAULT now(),
    due_date timestamp with time zone,
    completion_date timestamp with time zone,
    status text DEFAULT 'pending',
    progress_percentage integer DEFAULT 0,
    score integer,
    created_at timestamp with time zone DEFAULT now(),
    updated_at timestamp with time zone DEFAULT now()
);

-- STEP 3: ADD MISSING COLUMNS TO EXISTING TABLES
-- ========================================================================
ALTER TABLE public.edd_reviews ADD COLUMN IF NOT EXISTS edd_level text DEFAULT 'standard';
ALTER TABLE public.edd_reviews ADD COLUMN IF NOT EXISTS review_date timestamp with time zone;
ALTER TABLE public.edd_reviews ADD COLUMN IF NOT EXISTS reviewed_by uuid;
ALTER TABLE public.edd_reviews ADD COLUMN IF NOT EXISTS edd_findings text;

ALTER TABLE public.sanctions_screening ADD COLUMN IF NOT EXISTS criminal_hits integer DEFAULT 0;
ALTER TABLE public.sanctions_screening ADD COLUMN IF NOT EXISTS sanctions_hits integer DEFAULT 0;
ALTER TABLE public.sanctions_screening ADD COLUMN IF NOT EXISTS total_hits integer DEFAULT 0;
ALTER TABLE public.sanctions_screening ADD COLUMN IF NOT EXISTS provider text;
ALTER TABLE public.sanctions_screening ADD COLUMN IF NOT EXISTS result text;

ALTER TABLE public.ctr_filings ADD COLUMN IF NOT EXISTS customer_name text;
ALTER TABLE public.ctr_filings ADD COLUMN IF NOT EXISTS amount numeric(15, 2);
ALTER TABLE public.ctr_filings ADD COLUMN IF NOT EXISTS transaction_date date;
ALTER TABLE public.ctr_filings ADD COLUMN IF NOT EXISTS currency text DEFAULT 'USD';

ALTER TABLE public.sar_drafts ADD COLUMN IF NOT EXISTS subject_name text;
ALTER TABLE public.sar_drafts ADD COLUMN IF NOT EXISTS suspicious_activity_type text;
ALTER TABLE public.sar_drafts ADD COLUMN IF NOT EXISTS amount_involved numeric(15, 2);
ALTER TABLE public.sar_drafts ADD COLUMN IF NOT EXISTS activity_date date;

ALTER TABLE public.beneficial_owners ADD COLUMN IF NOT EXISTS address text;
ALTER TABLE public.beneficial_owners ADD COLUMN IF NOT EXISTS id_type text;
ALTER TABLE public.beneficial_owners ADD COLUMN IF NOT EXISTS politically_exposed boolean DEFAULT false;

ALTER TABLE public.kyc_submissions ADD COLUMN IF NOT EXISTS notes text;

-- STEP 4: DATA RETENTION JOBS
-- ========================================================================
-- pg_cron is not available on Neon. For automatic data retention, use:
-- - External scheduled jobs (GitHub Actions, AWS Lambda, Vercel Cron)
-- - Manual cleanup scripts run on schedule
-- - Contact Neon support to enable pg_cron on your project

-- Manual retention cleanup queries for your jurisdiction:
-- USA (FinCEN): 5-7 years
-- DELETE FROM public.sars WHERE status = 'filed' AND filed_date < now() - interval '5 years';
-- DELETE FROM public.ctrs WHERE status = 'filed' AND filing_date < now() - interval '5 years';
-- DELETE FROM public.kyc_audit_logs WHERE created_at < now() - interval '7 years';

-- EU (GDPR): 5 years
-- DELETE FROM public.transactions WHERE created_at < now() - interval '5 years';

-- UK (FCA): 5 years
-- DELETE FROM public.kyc_submissions WHERE status = 'closed' AND updated_at < now() - interval '5 years';

-- CANADA (FINTRAC): 5 years
-- DELETE FROM public.sars WHERE status = 'filed' AND filed_date < now() - interval '5 years';

-- AUSTRALIA (AUSTRAC): 7 years
-- DELETE FROM public.kyc_audit_logs WHERE created_at < now() - interval '7 years';

-- BRAZIL (COAF): 10 years
-- DELETE FROM public.kyc_submissions WHERE status = 'closed' AND updated_at < now() - interval '10 years';

-- HONG KONG & SINGAPORE: 5 years
-- DELETE FROM public.kyc_submissions WHERE status = 'closed' AND updated_at < now() - interval '5 years';

-- STEP 5: VERIFICATION
-- ========================================================================
-- Run these queries to verify schema was created correctly:
-- SELECT COUNT(*) FROM information_schema.tables WHERE table_schema = 'public';
-- SELECT COUNT(*) FROM pg_indexes WHERE schemaname = 'public';
-- SELECT EXISTS(SELECT 1 FROM information_schema.tables WHERE table_name='todos');

-- ✅ COMPLETE! All tables, columns, and indexes have been created successfully.
-- The database is now ready for use. Data retention must be managed separately.
`;

function getAPIConfig() {
  try {
    return JSON.parse(localStorage.getItem("apiConfig")) || { tmSystemUrl: "", apiKey: "", lastSync: null, syncEnabled: false };
  } catch {
    return { tmSystemUrl: "", apiKey: "", lastSync: null, syncEnabled: false };
  }
}

function saveAPIConfig(config) {
  localStorage.setItem("apiConfig", JSON.stringify(config));
}

function Field({ label, value, onChange, placeholder, type = "text" }) {
  const C = useTheme();
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.08em", fontFamily: "'DM Mono', monospace" }}>{label.toUpperCase()}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        style={{ background: C.bg, border: `1px solid ${focused ? C.accent : C.border}`, borderRadius: 8, color: C.text, fontSize: 14, padding: "10px 14px", fontFamily: "'DM Mono', monospace", outline: "none", width: "100%", boxSizing: "border-box", transition: "border-color 0.15s" }} />
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  const C = useTheme();
  const { t } = useTranslation();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.08em", fontFamily: "'DM Mono', monospace" }}>{label.toUpperCase()}</label>
      <select value={value} onChange={e => onChange(e.target.value)} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, color: value ? C.text : C.textDim, fontSize: 14, padding: "10px 14px", fontFamily: "'DM Mono', monospace", outline: "none", width: "100%" }}>
        <option value="">{t('common.select')}…</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function PasswordChangePanel({ currentUser }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  async function handleChangePassword() {
    setError(null);
    setMessage(null);

    if (!newPassword.trim()) {
      setError("Password is required");
      return;
    }

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: currentUser.email,
          newPassword: newPassword
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to change password");
      }

      setMessage("Password changed successfully!");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 22, gridColumn: "1 / -1" }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ color: C.text, fontWeight: 700, fontSize: 15 }}>Change Password</div>
        <div style={{ color: C.textDim, fontSize: 12, marginTop: 3 }}>Update your account password</div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <Field
          label="New Password"
          type="password"
          value={newPassword}
          onChange={setNewPassword}
          placeholder="Min 6 characters"
        />
        <Field
          label="Confirm Password"
          type="password"
          value={confirmPassword}
          onChange={setConfirmPassword}
          placeholder="Re-enter password"
        />

        {error && (
          <div style={{ background: "rgba(220,53,69,0.1)", border: "1px solid rgba(220,53,69,0.3)", borderRadius: 6, padding: "10px 12px", color: "#DC3545", fontSize: 12 }}>
            {error}
          </div>
        )}

        {message && (
          <div style={{ background: "rgba(40,167,69,0.1)", border: "1px solid rgba(40,167,69,0.3)", borderRadius: 6, padding: "10px 12px", color: "#28A745", fontSize: 12 }}>
            {message}
          </div>
        )}

        <button
          onClick={handleChangePassword}
          disabled={loading || !newPassword || !confirmPassword}
          style={{
            background: C.accent,
            color: C.bg,
            border: "none",
            borderRadius: 8,
            padding: "10px 16px",
            fontWeight: 700,
            fontSize: 13,
            cursor: loading || !newPassword || !confirmPassword ? "not-allowed" : "pointer",
            opacity: loading || !newPassword || !confirmPassword ? 0.6 : 1,
            transition: "opacity 0.2s"
          }}
        >
          {loading ? "Changing..." : "Change Password"}
        </button>
      </div>
    </div>
  );
}

function MFAPanel({ currentUser }) {
  const C = useTheme();
  const { t } = useTranslation();

  // MFA is not available in the current implementation (requires custom auth service)
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 22, gridColumn: "1 / -1" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
        <div>
          <div style={{ color: C.text, fontWeight: 700, fontSize: 15 }}>{t('settings.mfa.title')}</div>
          <div style={{ color: C.textDim, fontSize: 12, marginTop: 3 }}>{t('settings.mfa.subtitle')}</div>
        </div>
        <div style={{
          padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700,
          background: "rgba(255,140,0,0.1)",
          color: "#FF8C00",
          border: `1px solid rgba(255,140,0,0.3)`,
        }}>
          Not Available
        </div>
      </div>

      <div style={{ background: "rgba(255,140,0,0.08)", border: `1px solid #FF8C0040`, borderRadius: 7, padding: "12px 14px", color: "#FF8C00", fontSize: 12 }}>
        Multi-factor authentication requires a custom authentication service and is not available in the current implementation. Please contact support to enable this feature.
      </div>
    </div>
  );
}
function APISetupPanel({ company }) {
  const C = useTheme();
  const [showSetupGuide, setShowSetupGuide] = useState(false);

  const requiredAPIs = [
    { name: "Anthropic (Claude)", env: "VITE_ANTHROPIC_API_KEY", purpose: "AI analysis for KYC documents, data consistency checks, and compliance decisions" },
    { name: "Gemini (Google)", env: "VITE_GEMINI_API_KEY", purpose: "Vision AI for ID document authentication and facial recognition" },
  ];

  const optionalAPIs = [
    { name: "Groq", env: "VITE_GROQ_API_KEY", purpose: "Fast language model for real-time transaction analysis" },
    { name: "NamSor", env: "VITE_NAMSOR_API_KEY", purpose: "Name-based PEP and sanctions screening" },
    { name: "Dilisense", env: "VITE_DILISENSE_API_KEY", purpose: "Sanctions and watchlist screening (120+ lists)" },
    { name: "BlockScout", env: "VITE_BLOCKSCOUT_API_KEY", purpose: "Blockchain address and transaction data" },
    { name: "Ankr", env: "VITE_ANKR_API_KEY", purpose: "Multi-chain RPC provider for blockchain queries" },
    { name: "Chainalysis", env: "VITE_CHAINALYSIS_API_KEY", purpose: "Cryptocurrency risk scoring and AML compliance" },
    { name: "DefiLlama", env: "VITE_DEFILLAMA_API_KEY", purpose: "DeFi protocol intelligence and risk metrics" },
    { name: "Abstract API (Phone)", env: "VITE_ABSTRACT_API_KEY", purpose: "Phone number validation and intelligence" },
    { name: "HaveIBeenPwned", env: "VITE_HIBP_API_KEY", purpose: "Email breach and data compromise checking" },
    { name: "Serpro CNPJ (Brazil)", env: "SERPRO_CNPJ_API_KEY", purpose: "Legal entity verification and company registration data (Brazil)" },
    { name: "Serpro CPF (Brazil)", env: "SERPRO_CPF_API_KEY", purpose: "Individual taxpayer registration verification (Brazil)" },
    { name: "Serpro CND (Brazil)", env: "SERPRO_CND_API_KEY", purpose: "Tax clearance certificate verification (Certidões Negativas de Débitos)" },
    { name: "Serpro NFe (Brazil)", env: "SERPRO_NFE_API_KEY", purpose: "Electronic invoice consultation and verification (Brazil)" },
  ];

  const databaseEnvs = [
    { name: "Database URL (Neon)", env: "DATABASE_URL_UNPOOLED", required: true, purpose: "Direct PostgreSQL connection without pooling" },
    { name: "Prisma Database URL", env: "POSTGRES_PRISMA_URL", required: true, purpose: "Prisma ORM connection string" },
    { name: "Database Password", env: "POSTGRES_PASSWORD", required: true, purpose: "Neon database password" },
  ];

  const supabaseEnvs = [
    { name: "Supabase URL", env: "VITE_SUPABASE_URL", required: true, purpose: "Supabase project URL for auth & storage" },
    { name: "Supabase Anon Key", env: "VITE_SUPABASE_ANON_KEY", required: true, purpose: "Supabase public API key" },
  ];

  const companyEnvs = [
    { name: "Company Code", env: "VITE_COMPANY_CODE", required: true, purpose: "Company registration/activation code" },
    { name: "Proxy Secret", env: "VITE_PROXY_SECRET", required: true, purpose: "Server-side proxy authentication token" },
    { name: "Trial Code", env: "VITE_TRIAL_CODE", required: false, purpose: "Trial account activation code" },
  ];

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 22, gridColumn: "1 / -1" }}>
      <div style={{ marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "start" }}>
        <div>
          <div style={{ color: C.text, fontWeight: 700, fontSize: 15, marginBottom: 4 }}>API Configuration</div>
          <div style={{ color: C.textDim, fontSize: 12 }}>Add API keys to enable advanced features. See the setup guide below for detailed instructions.</div>
        </div>
        <button
          onClick={() => setShowSetupGuide(!showSetupGuide)}
          style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMid, padding: "4px 11px", cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace", whiteSpace: "nowrap" }}>
          {showSetupGuide ? "Hide Setup Guide" : "View Setup Guide"}
        </button>
      </div>

      {/* Setup Guide */}
      {showSetupGuide && (
        <div style={{ marginBottom: 28, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: 16 }}>
          <div style={{ color: C.text, fontWeight: 600, fontSize: 12, marginBottom: 12 }}>📋 Environment Variables Setup</div>

          {/* Database */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ color: C.textMid, fontSize: 11, fontWeight: 600, marginBottom: 8, letterSpacing: "0.05em" }}>DATABASE (NEON)</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {databaseEnvs.map(env => (
                <div key={env.env} style={{ display: "flex", alignItems: "start", gap: 10, fontSize: 10 }}>
                  <span style={{ color: env.required ? C.danger : C.success, fontWeight: 600, minWidth: 40 }}>{env.required ? "REQUIRED" : "OPTIONAL"}</span>
                  <div style={{ flex: 1 }}>
                    <code style={{ color: C.accent, fontFamily: "'DM Mono', monospace" }}>{env.env}</code>
                    <div style={{ color: C.textDim, fontSize: 9, marginTop: 2 }}>{env.purpose}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Supabase */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ color: C.textMid, fontSize: 11, fontWeight: 600, marginBottom: 8, letterSpacing: "0.05em" }}>SUPABASE</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {supabaseEnvs.map(env => (
                <div key={env.env} style={{ display: "flex", alignItems: "start", gap: 10, fontSize: 10 }}>
                  <span style={{ color: env.required ? C.danger : C.success, fontWeight: 600, minWidth: 40 }}>{env.required ? "REQUIRED" : "OPTIONAL"}</span>
                  <div style={{ flex: 1 }}>
                    <code style={{ color: C.accent, fontFamily: "'DM Mono', monospace" }}>{env.env}</code>
                    <div style={{ color: C.textDim, fontSize: 9, marginTop: 2 }}>{env.purpose}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Company Config */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ color: C.textMid, fontSize: 11, fontWeight: 600, marginBottom: 8, letterSpacing: "0.05em" }}>COMPANY CONFIGURATION</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {companyEnvs.map(env => (
                <div key={env.env} style={{ display: "flex", alignItems: "start", gap: 10, fontSize: 10 }}>
                  <span style={{ color: env.required ? C.danger : C.success, fontWeight: 600, minWidth: 40 }}>{env.required ? "REQUIRED" : "OPTIONAL"}</span>
                  <div style={{ flex: 1 }}>
                    <code style={{ color: C.accent, fontFamily: "'DM Mono', monospace" }}>{env.env}</code>
                    <div style={{ color: C.textDim, fontSize: 9, marginTop: 2 }}>{env.purpose}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ background: "rgba(100,200,255,0.08)", border: `1px solid rgba(100,200,255,0.25)`, borderRadius: 6, padding: 10, fontSize: 10, color: C.textMid, lineHeight: 1.5 }}>
            <strong>Setup:</strong> Add these to your .env.local (development) or Vercel environment settings (production). Never commit sensitive variables to version control.
          </div>
        </div>
      )}

      {/* Required APIs */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ color: C.text, fontWeight: 600, fontSize: 13, marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${C.border}` }}>
          🔴 Required APIs
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {requiredAPIs.map(api => (
            <div key={api.env} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: 6 }}>
                <div style={{ color: C.text, fontWeight: 600, fontSize: 12 }}>{api.name}</div>
                <code style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 4, padding: "2px 6px", fontSize: 10, color: C.accent, fontFamily: "'DM Mono', monospace" }}>{api.env}</code>
              </div>
              <div style={{ color: C.textDim, fontSize: 11, lineHeight: 1.5 }}>{api.purpose}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Optional APIs */}
      <div>
        <div style={{ color: C.text, fontWeight: 600, fontSize: 13, marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${C.border}` }}>
          ⚪ Optional APIs
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
          {optionalAPIs.map(api => (
            <div key={api.env} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: 12 }}>
              <div style={{ color: C.text, fontWeight: 600, fontSize: 12, marginBottom: 4 }}>{api.name}</div>
              <code style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 4, padding: "2px 6px", fontSize: 9, color: C.accent, fontFamily: "'DM Mono', monospace", display: "block", marginBottom: 6 }}>{api.env}</code>
              <div style={{ color: C.textDim, fontSize: 11, lineHeight: 1.5 }}>{api.purpose}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ background: "rgba(100,200,255,0.08)", border: `1px solid rgba(100,200,255,0.25)`, borderRadius: 8, padding: 12, marginTop: 24, fontSize: 11, color: C.textMid, lineHeight: 1.6 }}>
        💡 <strong>Tip:</strong> API keys are stored securely in environment variables. Never commit them to version control. Set them in your .env.local file or hosting platform's environment settings.
      </div>
    </div>
  );
}

function DataRetentionPanel({ company }) {
  const C = useTheme();
  const [configured, setConfigured] = useState(() => {
    try { return JSON.parse(localStorage.getItem("retention_policy_configured") || "false"); } catch { return false; }
  });
  const [showSQL, setShowSQL] = useState(false);
  const [copied, setCopied]   = useState(false);

  const jurisdictionRules = RETENTION_RULES[company?.jurisdiction] || RETENTION_RULES["Other"];

  function toggleConfigured() {
    const next = !configured;
    setConfigured(next);
    localStorage.setItem("retention_policy_configured", JSON.stringify(next));
  }

  function copySQL() {
    navigator.clipboard.writeText(RETENTION_SQL).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const jurisdictionInfo = {
    "United Kingdom (FCA)": "FCA AML Rules — 7-year minimum retention",
    "United States (FinCEN)": "FinCEN BSA — 31 CFR 1010.430 (7-year minimum)",
    "European Union (EBA)": "AMLD5 — 5-year minimum retention (3-year audit logs)",
    "Singapore (MAS)": "MAS Guidelines — 5-year minimum retention",
    "UAE (CBUAE)": "CBUAE AML — 7-year minimum retention",
    "Hong Kong (HKMA)": "HKMA Guidelines — 6-year minimum retention",
    "Cayman Islands (CIMA)": "CIMA AML — 6-year minimum retention",
    "Malta (MFSA)": "AMLD5 — 5-year minimum retention (3-year audit logs)",
    "Brazil (BCB)": "Brazilian Law 12683 — 5-year minimum retention",
    "Other": "FATF Standards — 5-7 year minimum retention"
  };

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 22, gridColumn: "1 / -1" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
        <div>
          <div style={{ color: C.text, fontWeight: 700, fontSize: 15 }}>Data Retention Policy</div>
          <div style={{ color: C.textDim, fontSize: 12, marginTop: 3 }}>{jurisdictionInfo[company?.jurisdiction] || jurisdictionInfo["Other"]}</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{
            padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700,
            background: configured ? "rgba(0,230,118,0.1)" : "rgba(255,68,68,0.1)",
            color: configured ? C.success : C.danger,
            border: `1px solid ${configured ? "rgba(0,230,118,0.3)" : "rgba(255,68,68,0.3)"}`,
          }}>
            {configured ? "✓ Configured" : "⚠ Not Configured"}
          </div>
          <button onClick={() => setShowSQL(v => !v)} style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMid, padding: "4px 11px", cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace" }}>
            {showSQL ? "Hide SQL" : "View Setup SQL"}
          </button>
        </div>
      </div>

      {/* Retention table */}
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: 14 }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${C.border}` }}>
            {["Table", "Record Type", "Retention", "Regulation"].map(h => (
              <th key={h} style={{ padding: "6px 10px", textAlign: "left", color: C.textDim, fontSize: 10, letterSpacing: "0.08em" }}>{h.toUpperCase()}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {jurisdictionRules.map(r => (
            <tr key={r.table} style={{ borderBottom: `1px solid ${C.border}` }}>
              <td style={{ padding: "7px 10px", color: C.accent, fontFamily: "'DM Mono', monospace", fontSize: 11 }}>{r.table}</td>
              <td style={{ padding: "7px 10px", color: C.text }}>{r.label}</td>
              <td style={{ padding: "7px 10px", color: r.years >= 7 ? C.warn : C.textMid, fontWeight: 600 }}>{r.years} years</td>
              <td style={{ padding: "7px 10px", color: C.textDim, fontSize: 11 }}>{r.regulation}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {showSQL && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ color: C.textMid, fontSize: 12, fontWeight: 600 }}>Supabase pg_cron Setup SQL</div>
            <button onClick={copySQL} style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 5, color: C.textMid, padding: "3px 10px", cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace" }}>
              {copied ? "✓ Copied" : "Copy"}
            </button>
          </div>
          <pre style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "14px 16px", fontSize: 11, color: C.textMid, overflowX: "auto", margin: 0, fontFamily: "'DM Mono', monospace", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
            {RETENTION_SQL}
          </pre>
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button onClick={toggleConfigured} style={{
          padding: "7px 16px", borderRadius: 7, cursor: "pointer", fontSize: 12,
          fontFamily: "'DM Mono', monospace", fontWeight: 600,
          background: configured ? "rgba(0,230,118,0.1)" : C.accent,
          border: configured ? "1px solid rgba(0,230,118,0.4)" : "none",
          color: configured ? C.success : C.bg,
        }}>
          {configured ? "✓ Mark as Configured" : "Mark as Configured"}
        </button>
        <span style={{ color: C.textDim, fontSize: 11 }}>
          Run the SQL above in Supabase → Admin → SQL Editor, then mark as configured.
        </span>
      </div>
    </div>
  );
}

function BYODSettingsPanel({ company, currentUser }) {
  const C = useTheme();
  const [byodEnabled, setByodEnabled] = useState(false);
  const [editing, setEditing] = useState(false);
  const [credentials, setCredentials] = useState({ host: "", port: "5432", database: "", username: "", password: "" });
  const [testingDB, setTestingDB] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load BYOD config on mount
  useEffect(() => {
    fetch(`/api/byod/config?companyId=${company.id}`, {
      headers: { "Authorization": `Bearer ${localStorage.getItem('amlguard_token')}` }
    })
      .then(r => r.json())
      .then(data => {
        if (data.success && data.config) {
          setByodEnabled(true);
          setCredentials({ ...credentials, host: data.config.host, port: data.config.port, database: data.config.database, username: data.config.username });
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const testConnection = async () => {
    setTestingDB(true);
    setTestResult(null);
    try {
      const res = await fetch("/api/byod/test-connection", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem('amlguard_token')}`
        },
        body: JSON.stringify({ ...credentials, companyId: company.id })
      });
      const data = await res.json();
      setTestResult(data.success ? { success: true, message: "✓ Connection successful" } : { success: false, message: data.error || "Connection failed" });
    } catch (err) {
      setTestResult({ success: false, message: `Error: ${err.message}` });
    } finally {
      setTestingDB(false);
    }
  };

  const saveCredentials = async () => {
    try {
      const res = await fetch("/api/byod/config", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem('amlguard_token')}`
        },
        body: JSON.stringify({ ...credentials, companyId: company.id })
      });
      const data = await res.json();
      if (data.success) {
        setByodEnabled(true);
        setEditing(false);
        setTestResult({ success: true, message: "✓ Database credentials saved securely" });
      } else {
        setTestResult({ success: false, message: data.error || "Failed to save credentials" });
      }
    } catch (err) {
      setTestResult({ success: false, message: `Error: ${err.message}` });
    }
  };

  if (loading) return null;

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 22, gridColumn: "1 / -1" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
        <div>
          <div style={{ color: C.text, fontWeight: 700, fontSize: 15 }}>Bring Your Own Database (BYOD)</div>
          <div style={{ color: C.textDim, fontSize: 12, marginTop: 3 }}>Connect your own Postgres database to keep your compliance data under your control</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{
            padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700,
            background: byodEnabled ? "rgba(0,230,118,0.1)" : "rgba(255,68,68,0.1)",
            color: byodEnabled ? C.success : C.danger,
            border: `1px solid ${byodEnabled ? "rgba(0,230,118,0.3)" : "rgba(255,68,68,0.3)"}`,
          }}>
            {byodEnabled ? "✓ Connected" : "⚠ Not Connected"}
          </div>
          <button onClick={() => setEditing(!editing)} style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMid, padding: "4px 11px", cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace" }}>
            {editing ? "Cancel" : "Configure"}
          </button>
        </div>
      </div>

      {editing ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ background: C.bg, border: `1px solid ${C.border}40`, borderRadius: 8, padding: 12 }}>
            <div style={{ color: C.textDim, fontSize: 11, marginBottom: 8 }}>🔒 Your database credentials are encrypted and never transmitted in plaintext</div>
          </div>

          <div>
            <label style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.08em", marginBottom: 6, display: "block" }}>DATABASE HOST</label>
            <input value={credentials.host} onChange={e => setCredentials({ ...credentials, host: e.target.value })} placeholder="postgres.example.com or localhost"
              style={{ width: "100%", padding: "10px 14px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13, fontFamily: "'DM Mono', monospace", outline: "none", boxSizing: "border-box" }} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.08em", marginBottom: 6, display: "block" }}>PORT</label>
              <input value={credentials.port} onChange={e => setCredentials({ ...credentials, port: e.target.value })} placeholder="5432"
                style={{ width: "100%", padding: "10px 14px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13, fontFamily: "'DM Mono', monospace", outline: "none", boxSizing: "border-box" }} />
            </div>
            <div>
              <label style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.08em", marginBottom: 6, display: "block" }}>DATABASE</label>
              <input value={credentials.database} onChange={e => setCredentials({ ...credentials, database: e.target.value })} placeholder="compliance_db"
                style={{ width: "100%", padding: "10px 14px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13, fontFamily: "'DM Mono', monospace", outline: "none", boxSizing: "border-box" }} />
            </div>
          </div>

          <div>
            <label style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.08em", marginBottom: 6, display: "block" }}>USERNAME</label>
            <input value={credentials.username} onChange={e => setCredentials({ ...credentials, username: e.target.value })} placeholder="postgres"
              style={{ width: "100%", padding: "10px 14px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13, fontFamily: "'DM Mono', monospace", outline: "none", boxSizing: "border-box" }} />
          </div>

          <div>
            <label style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.08em", marginBottom: 6, display: "block" }}>PASSWORD</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input type={showPassword ? "text" : "password"} value={credentials.password} onChange={e => setCredentials({ ...credentials, password: e.target.value })} placeholder="Your database password"
                style={{ flex: 1, padding: "10px 14px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13, fontFamily: "'DM Mono', monospace", outline: "none", boxSizing: "border-box" }} />
              <button onClick={() => setShowPassword(!showPassword)} style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMid, padding: "10px 12px", cursor: "pointer", fontSize: 12 }}>
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          <button onClick={testConnection} disabled={testingDB} style={{ padding: "10px 14px", background: C.accentDim, border: "none", borderRadius: 8, color: C.bg, fontWeight: 600, cursor: testingDB ? "default" : "pointer", fontFamily: "'DM Mono', monospace", fontSize: 12 }}>
            {testingDB ? "Testing…" : "Test Connection"}
          </button>

          {testResult && (
            <div style={{ padding: "10px 12px", borderRadius: 7, background: testResult.success ? "rgba(0,230,118,0.1)" : "rgba(255,68,68,0.1)", border: `1px solid ${testResult.success ? "rgba(0,230,118,0.3)" : "rgba(255,68,68,0.3)"}`, color: testResult.success ? C.success : C.danger, fontSize: 12 }}>
              {testResult.message}
            </div>
          )}

          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => setEditing(false)} style={{ flex: 1, padding: "10px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMid, cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 12 }}>
              Cancel
            </button>
            <button onClick={saveCredentials} style={{ flex: 1, padding: "10px", background: C.accent, border: "none", borderRadius: 8, color: C.bg, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 12 }}>
              Save & Encrypt
            </button>
          </div>
        </div>
      ) : byodEnabled ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${C.border}`, fontSize: 13 }}>
            <span style={{ color: C.textMid }}>Host</span>
            <span style={{ color: C.text, fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{credentials.host}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${C.border}`, fontSize: 13 }}>
            <span style={{ color: C.textMid }}>Port</span>
            <span style={{ color: C.text, fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{credentials.port}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", fontSize: 13 }}>
            <span style={{ color: C.textMid }}>Database</span>
            <span style={{ color: C.text, fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{credentials.database}</span>
          </div>
          <div style={{ marginTop: 12, padding: 12, background: C.bg, borderRadius: 8, fontSize: 11, color: C.textDim }}>
            ✓ Your data is stored in your own database. CrossRegGuard acts as a secure query processor with encrypted credential storage.
          </div>
        </div>
      ) : (
        <div style={{ color: C.textDim, fontSize: 12, padding: 12, background: C.bg, borderRadius: 8 }}>
          Click <strong>Configure</strong> to connect your own Postgres database and keep your compliance data on your infrastructure.
        </div>
      )}
    </div>
  );
}

export default function SettingsPage({ company, currentUser, setCompany, onSLAUpdate }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [editing, setEditing]               = useState(false);
  const [draft, setDraft]                   = useState({ ...company });
  const [apiEditing, setApiEditing]         = useState(false);
  const [apiConfig, setApiConfig]           = useState(getAPIConfig());
  const [apiDraft, setApiDraft]             = useState({ ...getAPIConfig() });
  const [testingConnection, setTestingConnection] = useState(false);
  const [testResult, setTestResult]         = useState(null);
  const [showApiKey, setShowApiKey]         = useState(false);
  const [activeTab, setActiveTab]           = useState("retention");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  function saveCompanyConfig() {
    saveCompany(draft);
    setCompany(draft);
    setEditing(false);
  }

  function saveAPIConfigHandler() {
    saveAPIConfig(apiDraft);
    setApiConfig(apiDraft);
    setApiEditing(false);
    setTestResult(null);
  }

  async function testConnection() {
    if (!apiDraft.tmSystemUrl) { setTestResult({ success: false, message: t('settings.api.enterUrl') }); return; }
    setTestingConnection(true);
    setTestResult(null);
    try {
      const base = apiDraft.tmSystemUrl.replace(/\/$/, "");
      const response = await fetch(`${base}/api/health`, {
        method: "GET",
        headers: apiDraft.apiKey ? { "Authorization": `Bearer ${apiDraft.apiKey}` } : {},
      });
      setTestResult(response.ok
        ? { success: true,  message: t('settings.api.connectionSuccess') }
        : { success: false, message: `${t('settings.api.connectionFailed')}: ${response.statusText}` }
      );
    } catch (error) {
      setTestResult({ success: false, message: `${t('settings.api.connectionError')}: ${error.message}` });
    } finally {
      setTestingConnection(false);
    }
  }

  const canSaveAPI = apiDraft.tmSystemUrl && apiDraft.apiKey;

  return (
    <div>
      <h1 style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontSize: 26, fontWeight: 700, margin: "0 0 24px" }}>{t('settings.title')}</h1>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>

        {/* Organisation card */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 22 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 18 }}>
            <div style={{ color: C.text, fontWeight: 700, fontSize: 15 }}>{t('settings.organisation')}</div>
            <button onClick={() => setEditing(!editing)} style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMid, padding: "4px 11px", cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace" }}>{editing ? t('common.cancel') : t('common.edit')}</button>
          </div>
          {editing ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Field label={t('settings.companyName')} value={draft.name || ""} onChange={v => setDraft(p => ({ ...p, name: v }))} />
              <SelectField label={t('settings.jurisdiction')} value={draft.jurisdiction || ""} onChange={v => setDraft(p => ({ ...p, jurisdiction: v }))} options={JURISDICTIONS} />
              <Field label={t('settings.licenceNo')} value={draft.licenseNo || ""} onChange={v => setDraft(p => ({ ...p, licenseNo: v }))} />
              <button onClick={saveCompanyConfig} style={{ padding: "9px", background: C.accent, border: "none", borderRadius: 7, color: C.bg, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 13 }}>{t('common.save')}</button>
            </div>
          ) : (
            <>
              {[[t('settings.companyName'), company?.name], [t('settings.type'), company?.type], [t('settings.jurisdiction'), company?.jurisdiction], [t('settings.licenceNo'), company?.licenseNo || "—"]].map(([k, v]) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${C.border}`, fontSize: 13 }}>
                  <span style={{ color: C.textMid }}>{k}</span>
                  <span style={{ color: C.text, fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{v}</span>
                </div>
              ))}
              {company?.join_code && currentUser?.permission === "admin" && (
                <div style={{ marginTop: 12, background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 16px" }}>
                  <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", marginBottom: 4 }}>{t('settings.joinCode').toUpperCase()}</div>
                  <div style={{ color: C.accent, fontFamily: "'DM Mono', monospace", fontSize: 24, fontWeight: 700, letterSpacing: "0.2em" }}>{company.join_code}</div>
                  <div style={{ color: C.textDim, fontSize: 11, marginTop: 4 }}>{t('settings.joinCodeHint')}</div>
                </div>
              )}
            </>
          )}
        </div>

        {/* API Integration card */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 22 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 18 }}>
            <div style={{ color: C.text, fontWeight: 700, fontSize: 15 }}>{t('settings.api.title')}</div>
            <button onClick={() => { setApiEditing(!apiEditing); setApiDraft({ ...apiConfig }); setTestResult(null); }} style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMid, padding: "4px 11px", cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace" }}>{apiEditing ? t('common.cancel') : t('settings.api.configure')}</button>
          </div>
          {apiEditing ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <Field label={t('settings.api.tmUrl')} value={apiDraft.tmSystemUrl || ""} onChange={v => setApiDraft(p => ({ ...p, tmSystemUrl: v }))} placeholder="https://tm-system.example.com" />
              <div>
                <div style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.08em", marginBottom: 6 }}>{t('settings.api.apiKey').toUpperCase()}</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <input type={showApiKey ? "text" : "password"} value={apiDraft.apiKey || ""} onChange={e => setApiDraft(p => ({ ...p, apiKey: e.target.value }))} placeholder={t('settings.api.enterApiKey')}
                    style={{ flex: 1, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 13, padding: "10px 12px", fontFamily: "'DM Mono', monospace", outline: "none" }} />
                  <button onClick={() => setShowApiKey(!showApiKey)} style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 7, color: C.textMid, cursor: "pointer", padding: "10px 12px", fontSize: 12 }}>{showApiKey ? t('common.hide') : t('common.show')}</button>
                </div>
              </div>
              {testResult && (
                <div style={{ padding: "10px 12px", borderRadius: 7, background: testResult.success ? "rgba(0,230,118,0.1)" : "rgba(255,68,68,0.1)", border: `1px solid ${testResult.success ? "rgba(0,230,118,0.3)" : "rgba(255,68,68,0.3)"}`, color: testResult.success ? C.success : C.danger, fontSize: 12 }}>
                  {testResult.message}
                </div>
              )}
              <button onClick={testConnection} disabled={!apiDraft.tmSystemUrl || testingConnection} style={{ padding: "8px 14px", background: apiDraft.tmSystemUrl ? C.accentDim : C.border, border: "none", borderRadius: 7, color: C.bg, fontWeight: 600, cursor: apiDraft.tmSystemUrl ? "pointer" : "default", fontFamily: "'DM Mono', monospace", fontSize: 12 }}>
                {testingConnection ? t('settings.api.testing') : t('settings.api.testConnection')}
              </button>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => setApiEditing(false)} style={{ flex: 1, padding: "8px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 7, color: C.textMid, cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{t('common.cancel')}</button>
                <button onClick={saveAPIConfigHandler} disabled={!canSaveAPI} style={{ flex: 1, padding: "8px", background: canSaveAPI ? C.accent : C.border, border: "none", borderRadius: 7, color: C.bg, fontWeight: 700, cursor: canSaveAPI ? "pointer" : "default", fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{t('settings.api.saveConfig')}</button>
              </div>
            </div>
          ) : (
            [[t('settings.api.tmUrl'), apiConfig.tmSystemUrl || t('settings.notConfigured')], [t('settings.api.apiKey'), apiConfig.apiKey ? "●●●●●●●●●●" : t('settings.notConfigured')], [t('settings.api.status'), apiConfig.tmSystemUrl ? t('settings.api.readyToSync') : t('settings.notConfigured')], [t('settings.api.lastSync'), apiConfig.lastSync ? new Date(apiConfig.lastSync).toLocaleString() : t('settings.api.never')]].map(([k, v]) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${C.border}`, fontSize: 13 }}>
                <span style={{ color: C.textMid }}>{k}</span>
                <span style={{ color: k === "Status" && apiConfig.tmSystemUrl ? C.success : C.text, fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{v}</span>
              </div>
            ))
          )}
        </div>
        {/* Password Change Panel */}
        {currentUser && (
          <PasswordChangePanel currentUser={currentUser} />
        )}

        {/* MFA disabled - not using Supabase for auth anymore */}
        {/* <MFAPanel currentUser={currentUser} /> */}

        {/* Admin Panels: Tabs */}
        {currentUser?.permission === "admin" && (
          <>
            {/* Tab selector */}
            <div style={{ gridColumn: "1 / -1", display: "flex", gap: 12, borderBottom: `1px solid ${C.border}`, marginBottom: 24 }}>
              <button
                onClick={() => setActiveTab("retention")}
                style={{
                  background: "transparent",
                  border: "none",
                  borderBottom: activeTab === "retention" ? `2px solid ${C.accent}` : "none",
                  color: activeTab === "retention" ? C.accent : C.textMid,
                  padding: "12px 4px",
                  cursor: "pointer",
                  fontWeight: activeTab === "retention" ? 600 : 400,
                  fontSize: 13,
                  fontFamily: "'DM Mono', monospace",
                  transition: "all 0.2s",
                }}
              >
                Data Retention Policy
              </button>
              <button
                onClick={() => setActiveTab("api")}
                style={{
                  background: "transparent",
                  border: "none",
                  borderBottom: activeTab === "api" ? `2px solid ${C.accent}` : "none",
                  color: activeTab === "api" ? C.accent : C.textMid,
                  padding: "12px 4px",
                  cursor: "pointer",
                  fontWeight: activeTab === "api" ? 600 : 400,
                  fontSize: 13,
                  fontFamily: "'DM Mono', monospace",
                  transition: "all 0.2s",
                }}
              >
                API Setup
              </button>
              <button
                onClick={() => setActiveTab("sla")}
                style={{
                  background: "transparent",
                  border: "none",
                  borderBottom: activeTab === "sla" ? `2px solid ${C.accent}` : "none",
                  color: activeTab === "sla" ? C.accent : C.textMid,
                  padding: "12px 4px",
                  cursor: "pointer",
                  fontWeight: activeTab === "sla" ? 600 : 400,
                  fontSize: 13,
                  fontFamily: "'DM Mono', monospace",
                  transition: "all 0.2s",
                }}
              >
                SLA Configuration
              </button>
              <button
                onClick={() => setActiveTab("monitoring")}
                style={{
                  background: "transparent",
                  border: "none",
                  borderBottom: activeTab === "monitoring" ? `2px solid ${C.accent}` : "none",
                  color: activeTab === "monitoring" ? C.accent : C.textMid,
                  padding: "12px 4px",
                  cursor: "pointer",
                  fontWeight: activeTab === "monitoring" ? 600 : 400,
                  fontSize: 13,
                  fontFamily: "'DM Mono', monospace",
                  transition: "all 0.2s",
                }}
              >
                Automated Monitoring
              </button>
              <button
                onClick={() => setActiveTab("sources")}
                style={{
                  background: "transparent",
                  border: "none",
                  borderBottom: activeTab === "sources" ? `2px solid ${C.accent}` : "none",
                  color: activeTab === "sources" ? C.accent : C.textMid,
                  padding: "12px 4px",
                  cursor: "pointer",
                  fontWeight: activeTab === "sources" ? 600 : 400,
                  fontSize: 13,
                  fontFamily: "'DM Mono', monospace",
                  transition: "all 0.2s",
                }}
              >
                Source Management
              </button>
              <button
                onClick={() => setActiveTab("risk")}
                style={{
                  background: "transparent",
                  border: "none",
                  borderBottom: activeTab === "risk" ? `2px solid ${C.accent}` : "none",
                  color: activeTab === "risk" ? C.accent : C.textMid,
                  padding: "12px 4px",
                  cursor: "pointer",
                  fontWeight: activeTab === "risk" ? 600 : 400,
                  fontSize: 13,
                  fontFamily: "'DM Mono', monospace",
                  transition: "all 0.2s",
                }}
              >
                Risk Assessment
              </button>
              <button
                onClick={() => setActiveTab("amlmodel")}
                style={{
                  background: "transparent",
                  border: "none",
                  borderBottom: activeTab === "amlmodel" ? `2px solid ${C.accent}` : "none",
                  color: activeTab === "amlmodel" ? C.accent : C.textMid,
                  padding: "12px 4px",
                  cursor: "pointer",
                  fontWeight: activeTab === "amlmodel" ? 600 : 400,
                  fontSize: 13,
                  fontFamily: "'DM Mono', monospace",
                  transition: "all 0.2s",
                }}
              >
                AML Model
              </button>
            </div>

            {/* Data Retention Policy panel */}
            {activeTab === "retention" && (
              <DataRetentionPanel company={company} />
            )}

            {/* API Setup panel */}
            {activeTab === "api" && (
              <APISetupPanel company={company} />
            )}

            {/* SLA Configuration panel */}
            {activeTab === "sla" && (
              <>
                <CaseRiskSLAConfig company={company} onSLAUpdate={onSLAUpdate} />
                <hr style={{ border: 'none', borderTop: `1px solid ${C.border}`, margin: '2rem 0' }} />
                <SLAConfiguration company={company} />
              </>
            )}

            {/* Monitoring Configuration panel */}
            {activeTab === "monitoring" && (
              <MonitoringConfiguration company={company} />
            )}

            {/* Source Management panel */}
            {activeTab === "sources" && (
              <SourceManagement company={company} />
            )}

            {/* Risk Configuration panel */}
            {activeTab === "risk" && (
              <RiskConfiguration company={company} />
            )}

            {/* AML Model Configuration panel */}
            {activeTab === "amlmodel" && (
              <AMLModelConfiguration company={company} />
            )}
          </>
        )}

        {/* BYOD: Bring Your Own Database */}
        {currentUser?.permission === "admin" && (
          <BYODSettingsPanel company={company} currentUser={currentUser} />
        )}

        {/* #27: GDPR / Data Subject Rights Export */}
        {currentUser?.permission === "admin" && (
          <GDPRExportPanel company={company} />
        )}
      </div>
    </div>
  );
}

// ─── GDPR / Data Subject Rights Export ───────────────────────────────────────
function GDPRExportPanel({ company }) {
  const C = useTheme();
  const [subjectName, setSubjectName] = useState("");
  const [exporting, setExporting] = useState(false);
  const [result, setResult] = useState(null);

  function handleExport() {
    if (!subjectName.trim()) return;
    setExporting(true);
    setResult(null);

    const query = subjectName.trim().toLowerCase();

    // Helper to search across all object values
    const matchesQuery = (obj) => {
      if (!obj) return false;
      return JSON.stringify(obj).toLowerCase().includes(query);
    };

    // Fetch all records from each table and filter client-side
    Promise.all([
      db.from("kyc_submissions").select("*").eq("company_id", company.id),
      db.from("sars").select("*").eq("company_id", company.id),
      db.from("ctrs").select("*").eq("company_id", company.id),
      db.from("cases").select("*").eq("company_id", company.id),
      db.from("customers").select("*").eq("company_id", company.id),
      db.from("audit_logs").select("*").eq("company_id", company.id),
    ])
      .then(([kycsRes, sarsRes, ctrsRes, casesRes, customersRes, auditRes]) => {
        // Broad client-side filtering - match if query appears anywhere in the record
        const filteredKycs = (kycsRes?.data || []).filter(matchesQuery);
        const filteredSars = (sarsRes?.data || []).filter(matchesQuery);
        const filteredCtrs = (ctrsRes?.data || []).filter(matchesQuery);
        const filteredCases = (casesRes?.data || []).filter(matchesQuery);
        const filteredCustomers = (customersRes?.data || []).filter(matchesQuery);
        const filteredAuditLogs = (auditRes?.data || []).filter(matchesQuery);

        const allData = {
          subject: subjectName.trim(),
          exported_at: new Date().toISOString(),
          search_term: query,
          kyc_records: filteredKycs,
          sar_records: filteredSars,
          ctr_records: filteredCtrs,
          case_records: filteredCases,
          customers: filteredCustomers,
          audit_logs: filteredAuditLogs,
        };

        const blob = new Blob([JSON.stringify(allData, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `data_subject_export_${subjectName.replace(/\s+/g, "_")}_${Date.now()}.json`;
        a.click();
        URL.revokeObjectURL(url);

        const total = filteredKycs.length + filteredSars.length + filteredCtrs.length + filteredCases.length +
                      filteredCustomers.length + filteredAuditLogs.length;
        setResult(`✓ Exported ${total} record(s) across all compliance modules.`);
        setExporting(false);
      })
      .catch(err => {
        console.warn("[Settings] GDPR export error:", err.message);
        setResult("Export failed. Please try again.");
        setExporting(false);
      });
  }

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 22, gridColumn: "1 / -1" }}>
      <div style={{ color: C.text, fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Data Subject Rights (GDPR/Privacy)</div>
      <div style={{ color: C.textDim, fontSize: 12, marginBottom: 16 }}>Export all data held about a specific individual to fulfil a Subject Access Request (SAR) or Right of Erasure inquiry.</div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <input value={subjectName} onChange={e => setSubjectName(e.target.value)} placeholder="Subject full name…"
          style={{ flex: 1, minWidth: 200, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, padding: "8px 12px", fontSize: 12, fontFamily: "'DM Mono',monospace", outline: "none" }} />
        <button onClick={handleExport} disabled={!subjectName.trim() || exporting}
          style={{ padding: "8px 18px", background: C.accent, border: "none", borderRadius: 7, color: C.bg, cursor: "pointer", fontSize: 12, fontFamily: "'DM Mono',monospace", fontWeight: 700 }}>
          {exporting ? "Exporting…" : "Export Data"}
        </button>
      </div>
      {result && <div style={{ marginTop: 10, color: C.success, fontSize: 12 }}>✓ {result}</div>}
      <div style={{ marginTop: 10, color: C.textDim, fontSize: 11 }}>Note: Review exported data before transmitting. Right of Erasure requests require legal review — do not delete compliance records subject to BSA/AML retention obligations.</div>
    </div>
  );
}

